import { createApiClient } from '../utils/api';

// API Configuration
const API_BASE_URL = 'https://api.test.ziz.co.ke';

// API Endpoints
export const ENDPOINTS = {
    CAPTURE_START: '/api/capture/start',
    CAPTURE_STOP: '/api/capture/stop',
    CAPTURE_STATUS: '/api/capture/status',
    STREAK_COUNT: '/api/streak_count', // POST body: { "numberOfDirections": 2 }
    LATEST_STREAK_COUNT: '/api/streak_count/latest', // GET
    AMOUNT: '/api/amount', // POST body: { "amount": number }
    LATEST_AMOUNT: '/api/amount/latest', // GET
    DIRECTIONS: '/api/directions', // GET
    ONGOING_TRADE: '/api/ongoing-trade', // GET - Get current trade status
    DIRECTION_HISTORY: '/api/direction-history' // GET - Get direction history with pagination, DELETE - Clear history
};

const api = createApiClient(API_BASE_URL);

/**
 * Start a new capture session
 * @returns {Promise<Object>} - The API response
 */
export const startCapture = async () => {
    return api.post(ENDPOINTS.CAPTURE_START);
};

/**
 * Stop the current capture session
 * @returns {Promise<Object>} - The API response
 */
export const stopCapture = async () => {
    return api.post(ENDPOINTS.CAPTURE_STOP);
};

/**
 * Get the current capture status
 * @returns {Promise<Object>} - The API response with capture status
 */
export const getCaptureStatus = async () => {
    return api.get(ENDPOINTS.CAPTURE_STATUS);
};

/**
 * Get streak count for a specific number of directions
 * @param {number} numberOfDirections - Number of directions to get streak count for
 * @returns {Promise<Object>} - The API response with streak count
 */
export const getStreakCount = async (numberOfDirections) => {
    return api.post(ENDPOINTS.STREAK_COUNT, { numberOfDirections });
};

/**
 * Get available directions
 * @returns {Promise<Object>} - The API response with directions
 */
export const getDirections = async () => {
    return api.get(ENDPOINTS.DIRECTIONS);
};

/**
 * Get the latest streak count
 * @returns {Promise<Object>} - The API response with latest streak count
 */
export const getLatestStreakCount = async () => {
    return api.get(ENDPOINTS.LATEST_STREAK_COUNT);
};

/**
 * Set a new amount
 * @param {number} amount - The amount to set
 * @returns {Promise<Object>} - The API response
 */
export const setAmount = async (amount) => {
    return api.post(ENDPOINTS.AMOUNT, { amount });
};

/**
 * Get the latest amount
 * @returns {Promise<Object>} - The API response with latest amount
 */
export const getLatestAmount = async () => {
    return api.get(ENDPOINTS.LATEST_AMOUNT);
};

/**
 * Get the current ongoing trade status
 * @returns {Promise<Object>} - The API response with trade status
 * @property {boolean} success - Whether there is an active trade
 * @property {string} direction - The trade direction ('UP' or 'DOWN')
 * @property {string} message - Status message about the trade
 */
export const getOngoingTradeStatus = async () => {
    return api.get(ENDPOINTS.ONGOING_TRADE);
};

/**
 * Get direction history with pagination
 * @param {number} page - Page number (1-based)
 * @param {number} limit - Number of items per page
 * @returns {Promise<Object>} - The API response with direction history
 */
export const getDirectionHistory = async (page = 1, limit = 10) => {
    const queryParams = new URLSearchParams({ page, limit });
    const url = `${ENDPOINTS.DIRECTION_HISTORY}?${queryParams.toString()}`;
    return api.get(url);
};

/**
 * Clear all direction history
 * @returns {Promise<Object>} - The API response
 */
export const clearDirectionHistory = async () => {
    return api.delete(ENDPOINTS.DIRECTION_HISTORY);
};

export default {
    startCapture,
    stopCapture,
    getCaptureStatus,
    getDirectionHistory,
    clearDirectionHistory,
    getStreakCount,
    getLatestStreakCount,
    setAmount,
    getLatestAmount,
    getOngoingTradeStatus,
    getDirections
};